'use client';
import React, { useEffect, useMemo, useState } from 'react';
import { MapPin, Calendar, Info, AlertTriangle } from 'lucide-react';
import type { OrderState } from './OrderWizardContent';
import { createClient } from '@/lib/supabase/client';

interface IssueOption {
  id: string;
  name: string;
  printDate: string;
  artworkDeadline: string;
  status: 'booking' | 'upcoming';
}

const fallbackIssues: IssueOption[] = [
  { id: 'issue-14', name: 'Issue #14', printDate: 'May 9, 2026', artworkDeadline: 'May 2, 2026', status: 'booking' },
  { id: 'issue-15', name: 'Issue #15', printDate: 'Jun 13, 2026', artworkDeadline: 'Jun 6, 2026', status: 'upcoming' },
  { id: 'issue-16', name: 'Issue #16', printDate: 'Jul 18, 2026', artworkDeadline: 'Jul 11, 2026', status: 'upcoming' },
];

interface Props {
  order: OrderState;
  updateOrder: (updates: Partial<OrderState>) => void;
  showErrors?: boolean;
}

export default function StepA({ order, updateOrder, showErrors }: Props) {
  const [issues, setIssues] = useState<IssueOption[]>(fallbackIssues);
  const supabase = createClient();
  const missingMarket = showErrors && !order.market;
  const missingIssue = showErrors && !order.issue;

  useEffect(() => {
    const loadIssues = async () => {
      const marketCode = order.market === 'puerto-rico' ? 'PR' : 'FL';
      const { data, error } = await supabase
        .from('issues')
        .select('id, issue_number, name, print_date, artwork_deadline, status, market')
        .eq('market', marketCode)
        .order('issue_number', { ascending: true });

      if (error || !data?.length) return;

      const mapped: IssueOption[] = data.map((row: any) => ({
        id: row.id,
        name: row.name || `Issue #${row.issue_number}`,
        printDate: row.print_date || 'TBD',
        artworkDeadline: row.artwork_deadline || 'TBD',
        status: row.status === 'booking' ? 'booking' : 'upcoming',
      }));

      setIssues(mapped);

      // If current selected issue is no longer valid for the new market, clear it
      if (order.issue && !mapped.find(i => i.id === order.issue)) {
        updateOrder({ issue: '', issueLabel: '', issuePrintDate: '', issueArtworkDeadline: '' });
      }
    };

    if (!order.market) return;
    void loadIssues();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.market]);

  const handleSelectIssue = (issue: IssueOption) => {
    updateOrder({
      issue: issue.id,
      issueLabel: issue.name,
      issuePrintDate: issue.printDate,
      issueArtworkDeadline: issue.artworkDeadline,
    });
  };

  const marketCards = useMemo(
    () => [
      { id: 'florida', label: 'Florida', emoji: '🌴', regions: '10 regions', desc: 'Jacksonville to Miami, Tampa to Panhandle' },
      { id: 'puerto-rico', label: 'Puerto Rico', emoji: '🇵🇷', regions: '2 regions', desc: 'Metro San Juan + Outside Metro' },
    ],
    []
  );

  return (
    <div className="animate-slide-up">
      <div className="mb-6">
        <h2 className="font-display text-2xl font-700 text-white mb-1">Select Market & Issue</h2>
        <p className="text-sm text-slate-400">Choose the market you want to advertise in and which issue to book.</p>
      </div>

      {/* Market selection */}
      <div className="mb-8">
        <label className="block text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
          <MapPin size={15} className="text-brand-400" />
          Market
          <span className="text-red-400">*</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {marketCards.map((m) => (
            <button
              key={`market-${m.id}`}
              onClick={() => updateOrder({ market: m.id as 'florida' | 'puerto-rico', regions: [], issue: '', issueLabel: '', issuePrintDate: '', issueArtworkDeadline: '' })}
              className={`text-left p-5 rounded-2xl border transition-all duration-200 ${
                order.market === m.id
                  ? 'border-brand-500/60 bg-brand-600/12 shadow-glow-sm'
                  : missingMarket
                  ? 'border-red-500/50 bg-navy-700/40 hover:border-brand-500/30 hover:bg-white/3' :'border-white/10 bg-navy-700/40 hover:border-brand-500/30 hover:bg-white/3'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl">{m.emoji}</span>
                <div>
                  <p className="font-semibold text-white">{m.label}</p>
                  <p className="text-xs text-brand-400 font-medium">{m.regions}</p>
                </div>
                {order.market === m.id && (
                  <div className="ml-auto w-5 h-5 rounded-full bg-brand-500 flex items-center justify-center">
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-500">{m.desc}</p>
            </button>
          ))}
        </div>
        {missingMarket && (
          <p className="mt-2 flex items-center gap-1.5 text-xs text-red-400">
            <AlertTriangle size={12} />
            Please select a market.
          </p>
        )}
      </div>

      {/* Issue selection */}
      <div>
        <label className="block text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
          <Calendar size={15} className="text-brand-400" />
          Issue
          <span className="text-red-400">*</span>
          <p className="text-xs text-slate-500 font-normal ml-1">— select the print run you want to appear in</p>
        </label>
        <div className="space-y-3">
          {issues.map((issue) => (
            <button
              key={`issue-${issue.id}`}
              onClick={() => handleSelectIssue(issue)}
              className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${
                order.issue === issue.id
                  ? 'border-brand-500/60 bg-brand-600/12'
                  : missingIssue
                  ? 'border-red-500/50 bg-navy-700/40 hover:border-brand-500/25 hover:bg-white/3' :'border-white/10 bg-navy-700/40 hover:border-brand-500/25 hover:bg-white/3'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${
                    order.issue === issue.id ? 'bg-gradient-primary text-white' : 'bg-navy-600 text-slate-400'
                  }`}>
                    {issue.name.split('#')[1]}
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">{issue.name}</p>
                    <div className="flex items-center gap-4 mt-0.5">
                      <span className="text-xs text-slate-500">Print: <span className="text-slate-400">{issue.printDate}</span></span>
                      <span className="text-xs text-slate-500">Artwork due: <span className="text-amber-400 font-medium">{issue.artworkDeadline}</span></span>
                    </div>
                  </div>
                </div>
                <span className={`chip text-xs ${issue.status === 'booking' ? 'bg-green-500/15 border-green-500/30 text-green-400' : 'bg-white/8 border-white/15 text-slate-500'}`}>
                  {issue.status === 'booking' ? 'Now Booking' : 'Upcoming'}
                </span>
              </div>
            </button>
          ))}
        </div>
        {missingIssue && (
          <p className="mt-2 flex items-center gap-1.5 text-xs text-red-400">
            <AlertTriangle size={12} />
            Please select an issue.
          </p>
        )}

        <div className="mt-4 flex items-start gap-2.5 p-3 rounded-xl bg-blue-500/8 border border-blue-500/20">
          <Info size={14} className="text-blue-400 shrink-0 mt-0.5" />
          <p className="text-xs text-slate-400">
            Artwork deadline is automatically set to 7 days before the print date. Missing the deadline may delay your placement to the next issue.
          </p>
        </div>
      </div>
    </div>
  );
}